import { Stack, Typography, useTheme } from "@mui/material";
import NoDataImage from "assets/images/noData.svg";
import {
  CircularLoader,
  DeleteConfirmationModal,
  HomeScreenLayout,
  MultiTabSwitch,
  SearchInput,
} from "components";
import {
  useConnectQb,
  useConnectStripe,
  useConnectXero,
  useCreateAccessRequest,
  useDisconnectHudu,
  useDisconnectIntegration,
  useDisconnectLevelIntegration,
  useDisconnectStripe,
  useGetHuduAuthConfig,
  useGetIntegrationSources,
  useGetPax8Settings,
  useGetStripeConnectStatus,
  useNonPersistantMainStore,
  useStartPax8Sync,
  useUpdateIntegrationStatus,
  useUpdateLevelStatus,
  useUpdateStripeStatus,
} from "controller";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  addMixpanelEvent,
  debugConsole,
  useMixpanelPageViewTracker,
} from "utilz/helpers";
import { LevelConnectionDrawer } from "./components";
import { DattoConnectionDrawer } from "./components/datto";
import { HuduConnectionDrawer } from "./components/hudu/connect-hudu-account/HuduConnectionDrawer";
import NinjaOneConnectionDrawer from "./components/ninja-one/NinjaOneConnectionDrawer";
import { IntegrationCard } from "./IntegrationCard";
import { type IntegrationListType, integrationsList } from "./integrationList";

/** The key the backend files Stripe beta requests under, per its own contract. */
// const STRIPE_ACCESS_FEATURE_NAME = "stripe";

/** The key the backend files Pax8 access requests under. */
const PAX8_ACCESS_FEATURE_NAME = "pax8";

/** Scope switch on the Live tab, replacing the old Filter button + chip. */
const INTEGRATION_SCOPE_OPTIONS = [
  { value: "mine", label: "My integrations" },
  { value: "all", label: "All" },
];

type StripeSourceRow = {
  name?: string;
  is_connected?: boolean;
  is_active?: boolean;
  synced_at?: string;
  charges_enabled?: boolean;
};

export const IntegrationHomeScreen = () => {
  useMixpanelPageViewTracker({ event: "integration_page_visited" });
  const navigate = useNavigate();
  const theme = useTheme();
  const featureFlag = useNonPersistantMainStore((state) => state.featureFlags);
  // STRIPE BETA GATE — PARKED. Stripe is open to every MSP, so the `stripe`
  // feature flag no longer decides "Connect" vs "Request access". Kept commented
  // rather than deleted so the gate can be restored if a rollback is needed:
  // uncomment this block, the STRIPE_ACCESS_FEATURE_NAME constant and the
  // card prop below, and fold both gates into the shared handler/prop
  // (`createAccessRequest` is already live for Pax8).
  //
  // // Stripe is beta-gated: MSPs without the flag get "Request access" instead of
  // // "Connect". Nothing records the request client-side, so the button stays
  // // available — a repeat press is harmless and better than a dead control.
  // const isStripeBetaGated = !featureFlag?.stripe;
  //
  // const handleRequestAccessClick = async () => {
  //   try {
  //     await createAccessRequest(STRIPE_ACCESS_FEATURE_NAME);
  //     // Product analytics only. The requester list itself now lives behind
  //     // GET /v1/access-requests, which is why no profile property is written.
  //     addMixpanelEvent({ event: "integration_stripe_access_requested" });
  //     toast.success(
  //       "Request received! We’ll notify you when beta access is enabled.",
  //     );
  //   } catch {
  //     // The hook's onError already reports to Sentry; the user just needs to
  //     // know it did not go through.
  //     toast.error("Could not send your request. Please try again.");
  //   }
  // };
  const { mutateAsync: createAccessRequest } = useCreateAccessRequest();

  // Pax8 is visible to every MSP; the `pax8` flag only decides "Connect" vs
  // "Request access". The flags API returns only flags that resolve true, so
  // an absent flag means gated. Nothing records the request client-side, so
  // the button stays available — the endpoint upserts, a repeat is harmless.
  const isPax8AccessGated = !featureFlag?.pax8;

  const handleRequestAccessClick = async () => {
    try {
      await createAccessRequest(PAX8_ACCESS_FEATURE_NAME);
      addMixpanelEvent({ event: "integration_pax8_access_requested" });
      toast.success(
        "Request received! We’ll notify you when beta access is enabled.",
      );
    } catch {
      // The hook's onError already reports to Sentry; the user just needs to
      // know it did not go through.
      toast.error("Could not send your request. Please try again.");
    }
  };
  const { mutate: startPax8Sync } = useStartPax8Sync();
  const [loadingCards, setLoadingCards] = useState<Array<string>>([]);
  const [activeIntegrations, setActiveIntegrations] = React.useState<any>([]);
  const [upcomingIntegrations, setUpcomingIntegrations] = React.useState<any>();
  const [selectedSource, setSelectedSource] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [ninjaDrawerState, setNinjaDrawerState] = useState<boolean>(false);
  const [levelDrawerState, setLevelDrawerState] = useState<boolean>(false);
  const [dattoDrawerState, setDattoDrawerState] = useState<boolean>(false);
  const [huduDrawerState, setHuduDrawerState] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [myIntegrationsOnly, setMyIntegrationsOnly] = useState<boolean>(false);

  const location = useLocation();
  useEffect(() => {
    if (location.state?.openHuduDrawer) {
      setHuduDrawerState(true);
    }
  }, []);
  useEffect(() => {
    const o = searchParams.get("o");
    if (o === "datto") {
      setDattoDrawerState(true);
    }
  }, [searchParams]);

  // "My integrations" is applied server-side: on the Live tab with the filter on,
  // fetch with ?filter=my_integrations so the API returns only the user's own.
  const showMyIntegrations = tabValue === 0 && myIntegrationsOnly;
  const {
    data: integrationSources,
    refetch: refetchSources,
    isLoading: isLoadingSources,
  } = useGetIntegrationSources(showMyIntegrations);

  // Whether this user has connected anything themselves. Until they have, the
  // scope switch offers a side that can only ever render blank, so it is hidden
  // and the screen stays on All.
  //
  // Asked of the API rather than matched locally on `created_by`: the filter is
  // the backend's rule (created_by === the authed user) and it owns it. This
  // shares a react-query key with the fetch above, so once "My integrations" is
  // selected the two collapse to a single request.
  const { data: myIntegrationSources, isLoading: isLoadingMyIntegrations } =
    useGetIntegrationSources(true);
  const hasMyIntegrations = (myIntegrationSources?.length ?? 0) > 0;

  // Disconnecting the last one takes the switch away with it; without this the
  // user would be stranded on a filter whose control no longer exists.
  useEffect(() => {
    if (!isLoadingMyIntegrations && !hasMyIntegrations && myIntegrationsOnly) {
      setMyIntegrationsOnly(false);
    }
  }, [isLoadingMyIntegrations, hasMyIntegrations, myIntegrationsOnly]);

  const { mutateAsync: connectQb } = useConnectQb();
  const { mutateAsync: connectXero } = useConnectXero();
  const { mutateAsync: connectStripe } = useConnectStripe();
  // Read at render scope because whether the Connect status is worth fetching
  // has to be decided before transformList runs, not inside it. That function
  // reads the same row via `integrationSources[index]`, like every other
  // integration.
  const stripeSource = (
    integrationSources as StripeSourceRow[] | undefined
  )?.find((integrationSource) => integrationSource.name === "stripe");

  const isPax8Connected = !!(
    integrationSources as { name?: string }[] | undefined
  )?.some((integrationSource) => integrationSource.name === "pax8");

  const { data: pax8Settings } = useGetPax8Settings({
    enabled: isPax8Connected,
  });

  // That endpoint queries Stripe live and costs ~1.4-2.9s, so it is only worth
  // paying for in the one case it can settle: Stripe connected, nothing synced
  // yet, and the stored copy already saying charges are off. Everything the
  // card needs otherwise comes from the sources row.
  const needsStripeConnectStatus =
    !!stripeSource &&
    !stripeSource.synced_at &&
    stripeSource.charges_enabled !== true;

  const { data: stripeStatus } = useGetStripeConnectStatus({
    enabled: needsStripeConnectStatus,
  });
  const { mutateAsync: disconnectStripe } = useDisconnectStripe();

  const { mutateAsync: updateStatus, isLoading: isUpdating } =
    useUpdateIntegrationStatus();
  const { mutateAsync: updateLevelStatus, isLoading: isLevelStatusUpdating } =
    useUpdateLevelStatus();
  const { mutateAsync: updateStripeStatus } = useUpdateStripeStatus();

  const { mutateAsync: disconnectIntegration } = useDisconnectIntegration();
  const { mutateAsync: disconnectLevelIntegration } =
    useDisconnectLevelIntegration();
  const { mutateAsync: disconnectHuduIntegration } = useDisconnectHudu();

  const { data: huduAuthConfig } = useGetHuduAuthConfig();

  useEffect(() => {
    if (integrationSources && integrationsList) {
      transformList();
    }
  }, [
    integrationSources,
    integrationsList,
    stripeStatus,
    huduAuthConfig,
    featureFlag,
  ]);

  const transformList = () => {
    if (integrationSources?.length < 1) {
      setUpcomingIntegrations(integrationsList);
      // Reset active so an empty result (e.g. "My integrations" with none, or a
      // fresh MSP) renders an empty grid — not stale cards from a prior fetch.
      setActiveIntegrations([]);
      return;
    }
    if (integrationSources && integrationsList) {
      const activeArray: Array<Record<string, any>> = [];
      const result = integrationsList.map((integration) => {
        const upcomingArray: Array<Record<string, any>> = [];
        integration.children.map((child) => {
          const index = integrationSources?.findIndex(
            (integrationSource: any) => integrationSource.name === child.value,
          );
          if (index > -1) {
            if (child.value === "stripe") {
              const isStripeConnected =
                integrationSources[index]?.is_connected ?? false;
              const isActive = integrationSources[index]?.is_active;
              const syncFailed =
                !integrationSources[index]?.synced_at &&
                integrationSources[index]?.charges_enabled !== true &&
                stripeStatus?.charges_enabled === false;
              activeArray.push({
                ...child,
                isAvailable: child.isAvailable,
                isConnected: isStripeConnected,
                isActive: isActive,
                menuItems: isStripeConnected,
                last_synced_at: integrationSources[index]?.synced_at,
                syncFailed,
              });
              return;
            }
            if (child.value === "xero") {
              activeArray.push({
                ...child,
                isAvailable: featureFlag.xero ? true : false,
                isConnected: integrationSources[index]?.is_connected,
                isActive: integrationSources[index]?.is_active,
                last_synced_at: integrationSources[index]?.synced_at,
                created_by: integrationSources[index]?.created_by,
              });
              return;
            }
            if (child.value === "hudu") {
              activeArray.push({
                ...child,
                isActive: integrationSources[index]?.is_active,
                isConnected:
                  integrationSources[index]?.is_connected ||
                  huduAuthConfig?.data?.has_api_key,
                last_synced_at: integrationSources[index]?.synced_at,
                created_by: integrationSources[index]?.created_by,
              });
              return;
            }
            activeArray.push({
              ...child,
              isActive: integrationSources[index]?.is_active,
              isConnected: integrationSources[index]?.is_connected,
              last_synced_at: integrationSources[index]?.synced_at,
              created_by: integrationSources[index]?.created_by,
            });
            return;
          }

          upcomingArray.push({
            ...child,
            isActive: false,
            isConnected: false,
          });
        });

        return {
          ...integration,
          children: upcomingArray,
        };
      });
      setUpcomingIntegrations(result);
      setActiveIntegrations(activeArray);
    }
  };

  const updateStatusFn = async ({
    source,
    status,
  }: {
    source: string;
    status: boolean;
  }) => {
    switch (source) {
      case "level":
        await updateLevelStatus(status);
        break;
      case "stripe":
        await updateStripeStatus(status);
        break;
      default:
        await updateStatus({ source, status });
    }
  };

  const handleToggleClick = async ({
    value,
    source,
  }: {
    value: boolean;
    source: string;
  }) => {
    try {
      await updateStatusFn({ source, status: value });
      setSelectedSource(null);
      toast.success(
        value === true
          ? "Integration activated successfully"
          : "Integration deactivated successfully",
      );
    } catch (error) {
      setSelectedSource(null);
      toast.error("Failed to update");
      debugConsole(error);
    }
  };

  // Reconnect swaps credentials without disconnecting, so it opens the same
  // credentials screen as a first connect. The backend keeps the MSP's
  // mappings and settings; only Disconnect clears them.
  const handleReconnectClick = ({ source }: { source: string }) => {
    switch (source) {
      case "pax8":
        navigate("pax8", { state: { mode: "update_credentials" } });
        break;
      default:
        navigate(source);
        break;
    }
  };

  const handleSyncNowClick = ({ source }: { source: string }) => {
    switch (source) {
      case "pax8":
        if (pax8Settings?.sync_started_at) {
          toast.error("A Pax8 sync is already running");
          return;
        }
        startPax8Sync(undefined, {
          onSuccess: () => toast.success("Pax8 sync started"),
          onError: () => toast.error("Couldn't start the Pax8 sync"),
        });
        break;
      default:
        break;
    }
  };

  const handleConnectClick = async ({
    source,
    isConnected,
  }: {
    source: string;
    isConnected: boolean;
  }) => {
    switch (source) {
      case "ninjaOne":
        if (isConnected) {
          navigate("ninjaOne");
        } else {
          setNinjaDrawerState(true);
          addMixpanelEvent({
            event: "connect_ninja_one_clicked",
          });
          // const oAuthLink = await NinjaOneOauth();
          // window.location.href = oAuthLink;
        }
        break;
      case "level":
        setLevelDrawerState(true);
        addMixpanelEvent({
          event: "connect_level_clicked",
        });
        break;
      case "datto":
        setDattoDrawerState(true);
        break;
      case "qb":
        try {
          if (isConnected) {
            navigate(`${source}/configure`, {
              state: { mode: "update" },
            });
          } else {
            const url = await connectQb();
            addMixpanelEvent({
              event: "connect_qb_clicked",
            });
            document.cookie = `qb_dd=${url.secret}; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
            document.cookie = `qb_dd=${url.secret}; Path=/;`;
            window.location.href = url.uri;
          }
        } catch (error) {
          toast.error("Failed to connect");
        }
        break;
      case "xero":
        try {
          if (isConnected) {
            navigate(`${source}/configure`, {
              state: { mode: "update" },
            });
          } else {
            const url = await connectXero();
            addMixpanelEvent({
              event: "connect_xero_clicked",
            });
            window.location.href = url.auth_url;
          }
        } catch (error: any) {
          toast.error(error?.message || "Failed to connect to Xero");
        }
        break;
      case "stripe":
        try {
          const res = await connectStripe();
          addMixpanelEvent({
            event: "connect_stripe_clicked",
          });
          window.location.href = res.uri;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : "Failed to connect to Stripe";
          toast.error(message);
        }
        break;
      case "ncentral":
        navigate(source);
        if (!isConnected) {
          addMixpanelEvent({
            event: "connect_ncentral_clicked",
          });
        }
        break;
      case "hudu":
        setHuduDrawerState(true);
        addMixpanelEvent({
          event: "connect_hudu_clicked",
        });
        break;
      case "pax8":
        // The auto-map settings screen is the first step of the connect wizard,
        // not a settings page you return to. Once the MSP has completed the
        // customer-mapping import they have been through it, so Configure goes
        // straight to the mapping they actually came back to work on.
        //
        // customers_last_imported_at is the marker: null = never imported.
        // Derived rather than a new column, so already-connected MSPs route
        // correctly with no migration and no backfill.
        if (isConnected) {
          navigate(
            pax8Settings?.customers_last_imported_at
              ? "pax8/mapping"
              : "pax8/configure",
          );
        } else {
          addMixpanelEvent({ event: "pax8_connection_started" });
          navigate("pax8");
        }
        break;
      default:
        navigate(source);
        break;
    }
  };

  const disconnectIntegrationFn = async (type: string) => {
    switch (type) {
      case "level":
        await disconnectLevelIntegration();
        break;
      case "hudu":
        await disconnectHuduIntegration();
        break;
      case "stripe":
        await disconnectStripe();
        addMixpanelEvent({ event: "stripe_disconnected" });
        break;
      case "pax8":
        await disconnectIntegration(type);
        addMixpanelEvent({ event: "pax8_connection_disconnected" });
        break;
      default:
        await disconnectIntegration(type);
    }
  };

  const menuItemClickFunction = async ({ type }: { type: string }) => {
    try {
      setSelectedSource(null);
      setLoadingCards((prev) => [...prev, type]);
      await disconnectIntegrationFn(type);
      toast.success("Integration disconnected successfully");
      setLoadingCards((prev) => prev.filter((item) => item !== type));
    } catch (error) {
      setSelectedSource(null);
      toast.error("An error occurred while processing your request.");
      debugConsole("Error in menuItemClickFunction:", error);
      setLoadingCards((prev) => prev.filter((item) => item !== type));
    }
  };

  const handleDisconnectClick = async ({
    value,
    source,
    type,
  }: {
    value: string;
    source: string;
    type: string;
  }) => {
    try {
      setSelectedSource({
        value,
        source,
        type,
        deactivationTagline:
          "Are you sure to disconnect the integration on this account?",
        clickType: "disconnect",
      });
    } catch (error) {}
  };

  const matchesSearch = (item: any) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      item?.label?.toLowerCase().includes(query) ||
      item?.description?.toLowerCase().includes(query)
    );
  };

  // "My integrations" is filtered server-side (fetch passes ?filter=
  // my_integrations); here we only apply the free-text search over the result.
  const displayedActiveIntegrations = (activeIntegrations || []).filter(
    (item: any) => matchesSearch(item),
  );

  // Upcoming: keep categories that still have at least one card after search.
  const displayedUpcomingIntegrations = (upcomingIntegrations || [])
    .map((category: any) => ({
      ...category,
      children: (category?.children || []).filter(matchesSearch),
    }))
    .filter((category: any) => category?.children?.length > 0);

  // What's New "Connect Stripe" lands here with ?o=stripe: scroll to the Stripe
  // card once the list has rendered it, then drop the param so a refetch doesn't
  // scroll again. Connecting stays on the card's own button.
  //
  // Waits a frame: clicked from this page, the What's New popover closes in the
  // same commit, and a scroll started inside that commit doesn't take.
  const stripeCardRef = useRef<HTMLDivElement>(null);
  const hasStripeCard = activeIntegrations.some(
    (item: IntegrationListType) => item.value === "stripe",
  );
  useEffect(() => {
    if (searchParams.get("o") !== "stripe") return;
    // Stripe is on Live; switching re-runs this once its card has rendered.
    if (tabValue !== 0) {
      setTabValue(0);
      return;
    }
    const card = hasStripeCard ? stripeCardRef.current : null;
    if (!card) return;
    const frame = requestAnimationFrame(() => {
      card.scrollIntoView({ behavior: "smooth", block: "center" });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("o");
          return next;
        },
        { replace: true },
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [searchParams, tabValue, hasStripeCard, setSearchParams]);

  const renderIntegrationCard = (child: any) => (
    <IntegrationCard
      key={child.value}
      cardRef={child.value === "stripe" ? stripeCardRef : undefined}
      onClick={({ value, source }) => {
        value
          ? handleToggleClick({ value, source })
          : setSelectedSource({
              value,
              source,
              deactivationTagline: child.deactivationTagline,
              clickType: "deactivate",
            });
      }}
      onConnectClick={handleConnectClick}
      // Stripe beta gate — parked, see the note above.
      // requiresAccessRequest={child.value === "stripe" && isStripeBetaGated}
      requiresAccessRequest={child.value === "pax8" && isPax8AccessGated}
      onRequestAccessClick={handleRequestAccessClick}
      onMenuItemClick={({ name, value, type }) => {
        // "Configure" routes through the per-integration connect handler (switch
        // scoped to each source); everything else is a disconnect action.
        if (value === "configure") {
          handleConnectClick({ source: type, isConnected: true });
          return;
        }
        if (value === "reconnect") {
          handleReconnectClick({ source: type });
          return;
        }
        if (value === "sync_now") {
          handleSyncNowClick({ source: type });
          return;
        }
        handleDisconnectClick({ value, source: name, type });
      }}
      {...child}
      value={child.value}
      sourceId={child.sourceId}
      isLoadingCard={loadingCards.includes(child.value)}
    />
  );

  return (
    <HomeScreenLayout
      title="Integrations"
      tabs={["Live", "Upcoming"]}
      tabValue={tabValue}
      onTabChange={(newValue) => setTabValue(newValue)}
      rightActionComponent={
        <SearchInput
          name="search"
          placeholder="Search"
          value={searchQuery}
          onChange={(e: any) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
        />
      }
      rightActionComponentTabLayer={
        tabValue === 0 && hasMyIntegrations ? (
          <Stack direction="row" sx={{ marginLeft: "auto" }}>
            <MultiTabSwitch
              options={INTEGRATION_SCOPE_OPTIONS}
              selectedValue={myIntegrationsOnly ? "mine" : "all"}
              onChange={(value) => setMyIntegrationsOnly(value === "mine")}
            />
          </Stack>
        ) : null
      }
    >
      {isLoadingSources ? (
        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularLoader />
        </Stack>
      ) : tabValue === 0 ? (
        <Stack
          p={"24px 32px 32px 32px"}
          sx={{
            borderTop: `1px solid ${theme.palette.neutral["015"]}`,
            flex: 1,
            minHeight: 0,
          }}
        >
          {displayedActiveIntegrations.length === 0 ? (
            <Stack
              sx={{
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <img src={NoDataImage} alt="no data" style={{ width: "200px" }} />
              {/* Two different emptinesses: a search that matched nothing is
                  the user's own doing and is fixed by clearing it, while an
                  empty list is a state of the account. Saying "no integrations"
                  to someone mid-search reads as though theirs disappeared. */}
              <Typography variant="body-large">
                {searchQuery.trim()
                  ? "No integrations match your search"
                  : "No integrations connected yet"}
              </Typography>
            </Stack>
          ) : (
            <Stack direction={"row"} sx={{ gap: "24px", flexWrap: "wrap" }}>
              {displayedActiveIntegrations.map((child: IntegrationListType) =>
                renderIntegrationCard(child),
              )}
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack
          p={"24px 32px 32px 32px"}
          gap="24px"
          sx={{ borderTop: `1px solid ${theme.palette.neutral["015"]}` }}
        >
          {displayedUpcomingIntegrations?.map(
            (integration: Record<string, any>) => (
              <Stack key={integration.type} sx={{ gap: "16px" }}>
                <Typography
                  variant="caption-3"
                  sx={{
                    color: theme.palette.lightBg.low,
                    textTransform: "uppercase",
                  }}
                >
                  {integration.type}
                </Typography>
                <Stack direction={"row"} sx={{ gap: "24px", flexWrap: "wrap" }}>
                  {integration.children.map((child: any) =>
                    renderIntegrationCard(child),
                  )}
                </Stack>
              </Stack>
            ),
          )}
        </Stack>
      )}

      {selectedSource && (
        <DeleteConfirmationModal
          onClose={() => setSelectedSource(null)}
          onSecondaryButtonClick={() => setSelectedSource(null)}
          open={selectedSource !== null}
          title={`Are you sure you want to ${selectedSource?.clickType}?`}
          tagline={selectedSource?.deactivationTagline}
          primaryButtonText={
            selectedSource?.clickType === "disconnect"
              ? "Disconnect"
              : "Deactivate"
          }
          onPrimaryButtonClick={() =>
            selectedSource?.clickType === "disconnect"
              ? menuItemClickFunction({
                  type: selectedSource?.type,
                })
              : handleToggleClick({
                  value: selectedSource?.value,
                  source: selectedSource?.source,
                })
          }
          paperStyles={{
            width: "370px",
          }}
          isLoading={isUpdating || isLevelStatusUpdating}
        />
      )}
      {ninjaDrawerState && (
        <NinjaOneConnectionDrawer
          open={ninjaDrawerState}
          onOpen={() => {}}
          onClose={() => setNinjaDrawerState(false)}
        />
      )}
      {levelDrawerState && (
        <LevelConnectionDrawer
          open={levelDrawerState}
          onOpen={() => {}}
          onClose={() => setLevelDrawerState(false)}
        />
      )}
      {dattoDrawerState && (
        <DattoConnectionDrawer
          open={dattoDrawerState}
          onOpen={() => {}}
          onClose={() => setDattoDrawerState(false)}
        />
      )}
      {huduDrawerState && (
        <HuduConnectionDrawer
          open={huduDrawerState}
          onOpen={() => {}}
          onClose={() => setHuduDrawerState(false)}
        />
      )}
    </HomeScreenLayout>
  );
};
